import {
  defaultOutcomeDefinitions,
  getOutcomeLabelsMissingFromContext,
  hasCompleteOutcomeDefinition,
  incompleteOutcomeTerminologyReason,
  isContactCustomFieldPresenceOutcomeDefinition,
  isFunnelOutcomeDefinition,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
  type OutcomeGreenRedYellowResultCategory,
} from "@repo/app-types/entities/outcome";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { outcomeDraftChangesRubric } from "@repo/app-types/entities/playbookConfigurationTools";
import type { PlaybookOutcomeUpdate } from "@repo/app-types/entities/playbookProposalDetail";
import type { PublishGlobalOnlyChange } from "@repo/app-types/helpers/playbookNodePublish";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { Pill } from "@repo/ui/Pills/pill";
import {
  IconAlertTriangle,
  IconPencil,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useRef, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import type { SampleToolResultOutcomeEntry } from "../../hooks/useSampleToolResults.js";
import { CopilotProposalBanner } from "../copilotProposalBanner.js";
import { FieldBackedOutcomeFields } from "./fieldBackedOutcomeFields.js";
import { getOutcomeCategoryPillColor } from "./outcomeCategoryPill.js";
import { OutcomeFields } from "./outcomeFields.js";
import { OutcomeLabelsEditorSlideoverContainer } from "./outcomeLabelsEditorSlideoverContainer.js";
import {
  OutcomeSourceChooserDialog,
  type OutcomeSource,
} from "./outcomeSourceChooserDialog.js";
import { OutcomeSuggestionResults } from "./outcomeSuggestionResults.js";
import { PublishSettingButton } from "./publishSettingButton.js";

const outcomeSource = {
  transcript: "transcript",
  field: "field",
} as const;

export interface OutcomeSettingsFormProps {
  outcome: OutcomeDefinition | null;
  initialOutcome: OutcomeDefinition | null;
  /**
   * Live/published outcome. Used as the diff baseline for the context
   * "Show changes" toggle in non-suggestion mode.
   */
  liveOutcome?: OutcomeDefinition | null;
  onUpdateOutcome: (outcome: OutcomeDefinition | null) => void;
  isReadOnly: boolean;
  /**
   * The agent's saved sample-tool result for the outcome, if any. When the
   * candidate context differs from the current outcome context, the form
   * renders in suggestion mode: banner + read-only form + Results pane.
   */
  suggestion?: SampleToolResultOutcomeEntry;
  /** Apply the agent's proposed context. Called by the suggestion banner. */
  onApplySuggestion?: (mergedOutcome: OutcomeDefinition) => void;
  /** Drop the saved suggestion for the outcome (used by Apply & Discard). */
  onDiscardSuggestion?: () => void;
  /** Ask the AI assistant to optimize the outcome rubric (its context). */
  onOptimizeOutcome?: (outcome: OutcomeDefinition) => void;
  /** Ask the AI assistant to run the sample-distribution tool against the outcome as-is. */
  onTestOutcome?: (outcome: OutcomeDefinition) => void;
  /** Whether Ask Alpharun is replying or loading a thread (gates Optimize/Test). */
  isCopilotBusy?: boolean;
  /** Absent outside the standalone playbook editor. */
  onPublishOutcome?: () => void;
  hasChangesToPublish?: boolean;
  isPublishBlocked?: boolean;
  isPublishing?: boolean;
  globalOnlyChanges?: readonly PublishGlobalOnlyChange[];
  playbookId?: PlaybookId | null;
  /** Show the draft indicator beside the shared Outcome heading. */
  hasUnpublishedChanges?: boolean;
  /**
   * Voice agents always derive outcomes from the transcript, so they reuse the
   * transcript editor without exposing the source label or CRM switcher.
   */
  isTranscriptOnly?: boolean;
  /**
   * Fill a bounded parent (the assessment editor's settings pane), letting the
   * context field scroll internally. Pass `false` on a flowing page so the form
   * grows with its content and the page is the only scroll region.
   * @default true
   */
  fillsHeight?: boolean;
}

export const OutcomeSettingsForm: FC<OutcomeSettingsFormProps> = ({
  outcome,
  initialOutcome,
  liveOutcome,
  onUpdateOutcome,
  isReadOnly,
  suggestion,
  onApplySuggestion,
  onDiscardSuggestion,
  onOptimizeOutcome,
  onTestOutcome,
  isCopilotBusy = false,
  onPublishOutcome,
  hasChangesToPublish = false,
  isPublishBlocked = false,
  isPublishing = false,
  globalOnlyChanges = [],
  playbookId,
  hasUnpublishedChanges = false,
  isTranscriptOnly = false,
  fillsHeight = true,
}) => {
  // Keep the source selection local until the CRM path has a field-backed
  // definition that can be committed.
  const [pendingSource, setPendingSource] = useState<OutcomeSource | null>(
    null,
  );
  const [isEditingLabels, setIsEditingLabels] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
    useState(false);
  const [isSourceChooserOpen, setIsSourceChooserOpen] = useState(false);
  const [showFooterDivider, setShowFooterDivider] = useState(false);
  const footerScrollContainerRef = useRef<HTMLDivElement>(null);
  const defaultTranscriptOutcome = defaultOutcomeDefinitions[0] ?? null;

  const lastTranscriptOutcomeRef = useRef<OutcomeDefinition | null>(
    outcome && !isFieldBackedOutcome(outcome)
      ? outcome
      : initialOutcome && !isFieldBackedOutcome(initialOutcome)
        ? initialOutcome
        : defaultTranscriptOutcome,
  );

  // Keep the restore target in sync when the parent replaces the outcome
  // (for example after discarding a draft or navigating between playbooks).
  // Without this, switching back from CRM could restore a transcript outcome
  // that belonged to an earlier render.
  useEffect(() => {
    if (outcome && !isFieldBackedOutcome(outcome)) {
      lastTranscriptOutcomeRef.current = outcome;
    }
  }, [outcome]);

  const isSuggestionMode =
    suggestion !== undefined &&
    outcome !== null &&
    isOutcomeInSuggestionMode(outcome, suggestion);

  const displayOutcome =
    isSuggestionMode && outcome
      ? mergeSuggestionIntoOutcome(
          outcome,
          suggestion.update?.candidate_draft ?? {},
        )
      : outcome;

  // Diff baseline for the context "Show changes" toggle. In suggestion mode the
  // diff shows current → proposed (baseline = the current outcome); otherwise it
  // shows the unpublished draft vs the live/published outcome.
  const diffBaselineOutcome = isSuggestionMode
    ? outcome
    : (liveOutcome ?? null);

  // In suggestion mode the form is a read-only preview of the proposed state.
  const effectiveReadOnly = isReadOnly || isSuggestionMode;

  const savedSource = getOutcomeSource(displayOutcome);
  const activeSource = pendingSource ?? savedSource;
  const isTranscriptOutcome = activeSource === outcomeSource.transcript;
  const isFieldBackedSource = activeSource === outcomeSource.field;
  const shouldFillFormHeight = fillsHeight && isTranscriptOutcome;

  useEffect(() => {
    const scrollContainer = footerScrollContainerRef.current;
    if (!scrollContainer || !fillsHeight || isSuggestionMode) {
      setShowFooterDivider(false);
      return;
    }

    const updateFooterDivider = (): void => {
      const remainingScroll =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      setShowFooterDivider(remainingScroll > 1);
    };

    updateFooterDivider();
    scrollContainer.addEventListener("scroll", updateFooterDivider, {
      passive: true,
    });
    window.addEventListener("resize", updateFooterDivider);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateFooterDivider);
    resizeObserver?.observe(scrollContainer);
    if (scrollContainer.firstElementChild instanceof HTMLElement) {
      resizeObserver?.observe(scrollContainer.firstElementChild);
    }

    return () => {
      scrollContainer.removeEventListener("scroll", updateFooterDivider);
      window.removeEventListener("resize", updateFooterDivider);
      resizeObserver?.disconnect();
    };
  }, [activeSource, fillsHeight, isSuggestionMode, suggestion]);

  const handleOutcomeUpdate = (nextOutcome: OutcomeDefinition | null): void => {
    if (nextOutcome && !isFieldBackedOutcome(nextOutcome)) {
      lastTranscriptOutcomeRef.current = nextOutcome;
    }
    onUpdateOutcome(nextOutcome);
  };

  const handleSourceChange = (source: string): void => {
    if (source === outcomeSource.field) {
      if (displayOutcome && !isFieldBackedOutcome(displayOutcome)) {
        lastTranscriptOutcomeRef.current = displayOutcome;
      }
      setPendingSource(
        displayOutcome && isFieldBackedOutcome(displayOutcome)
          ? null
          : outcomeSource.field,
      );
      return;
    }

    // If the user was only previewing the CRM setup for an existing transcript
    // outcome, switching back should simply cancel that pending change.
    if (
      pendingSource === outcomeSource.field &&
      displayOutcome &&
      !isFieldBackedOutcome(displayOutcome)
    ) {
      setPendingSource(null);
      return;
    }

    const nextTranscriptOutcome =
      lastTranscriptOutcomeRef.current ?? defaultOutcomeDefinitions[0];
    if (!nextTranscriptOutcome) return;
    setPendingSource(null);
    handleOutcomeUpdate(nextTranscriptOutcome);
  };

  const resultsToDisplay:
    | readonly {
        category: OutcomeGreenRedYellowResultCategory;
        label: string;
      }[]
    | null =
    displayOutcome &&
    (isTranscriptOutcome || isRevenueOutcomeDefinition(displayOutcome))
      ? displayOutcome.results
      : null;

  // The pencil edits the committed outcome's labels. Hide it while a
  // field-backed outcome is pending (no committed outcome to edit yet) and for
  // Funnel, whose labels come from its stage editor. Also hidden while
  // previewing an agent suggestion (read-only).
  const canEditLabels =
    !effectiveReadOnly &&
    pendingSource === null &&
    !!outcome &&
    !isFunnelOutcomeDefinition(outcome);

  // Nudge the user when the context no longer mentions every customizable
  // outcome label (e.g. after a rename). Only non-field-backed outcomes have a
  // context field, so skip revenue and funnel. Suppressed while previewing a
  // suggestion (the displayed context is the proposed one, not the committed).
  const showContextWarning =
    !effectiveReadOnly &&
    !!outcome &&
    isTranscriptOutcome &&
    getOutcomeLabelsMissingFromContext(outcome).length > 0;

  const outcomeHeading = (
    <h3 className="flex items-center gap-1.5 text-base font-semibold text-foreground">
      Outcome
      {hasUnpublishedChanges ? (
        <span
          aria-label="Outcome has unpublished changes"
          className="size-1.5 rounded-full bg-amber-500"
        />
      ) : null}
    </h3>
  );

  const formBody = (
    <div
      className={twMerge(
        "flex flex-col space-y-4 p-2",
        shouldFillFormHeight && "min-h-0 flex-1",
      )}
    >
      {showContextWarning ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4">
          <IconAlertTriangle
            aria-hidden
            className="mt-0.5 size-5 shrink-0 text-amber-500"
          />
          <div>
            <h3 className="text-sm font-medium text-amber-800">
              Values missing from instructions
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              Review the instructions below and make sure they explain when the
              agent should choose each outcome value.
            </p>
          </div>
        </div>
      ) : null}

      {activeSource ? (
        <div>
          {outcomeHeading}
          {!isTranscriptOnly ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              <span>
                {isFieldBackedSource
                  ? "Based on a synced CRM field"
                  : "Based on the call transcript"}
              </span>
              {!effectiveReadOnly ? (
                <>
                  <span aria-hidden>·</span>
                  <Button
                    variant={ButtonVariant.Tertiary}
                    size={Size.SMALL}
                    className="h-auto px-0 text-primary hover:text-indigo-500"
                    onClick={() => setIsSourceChooserOpen(true)}
                  >
                    Change
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="max-w-2xl py-2">
          {outcomeHeading}
          <p className="mt-1 text-sm text-muted-foreground">
            No outcome is configured for this playbook.
          </p>
          {!effectiveReadOnly && defaultTranscriptOutcome ? (
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              className="mt-4"
              onClick={() => handleOutcomeUpdate(defaultTranscriptOutcome)}
            >
              Add outcome
            </Button>
          ) : null}
        </div>
      )}

      {isFieldBackedSource ? (
        <FieldBackedOutcomeFields
          outcome={displayOutcome}
          onUpdateOutcome={(updatedOutcome) => {
            setPendingSource(null);
            handleOutcomeUpdate(updatedOutcome);
          }}
          isReadOnly={effectiveReadOnly}
        />
      ) : null}

      {resultsToDisplay ? (
        <div>
          <Label size={Size.SMALL}>Values</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {resultsToDisplay.map((result, index) => (
              <Pill
                key={index}
                label={result.label}
                color={getOutcomeCategoryPillColor(result.category)}
                size={Size.SMALL}
              />
            ))}
            {canEditLabels ? (
              <IconButton
                variant={ButtonVariant.Tertiary}
                size={Size.SMALL}
                icon={<IconPencil className="size-4" />}
                accessibleLabel="Edit values"
                onClick={() => setIsEditingLabels(true)}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {outcome && resultsToDisplay ? (
        <OutcomeLabelsEditorSlideoverContainer
          outcome={outcome}
          isOpen={isEditingLabels}
          onClose={() => setIsEditingLabels(false)}
          onSave={(updatedOutcome) => {
            handleOutcomeUpdate(updatedOutcome);
            setIsEditingLabels(false);
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={isRemoveConfirmationOpen}
        onOpenChange={setIsRemoveConfirmationOpen}
        title="Delete this outcome?"
        confirmText="Delete outcome"
        variant={AlertDialogVariant.Destructive}
        onConfirm={() => {
          setPendingSource(null);
          handleOutcomeUpdate(null);
        }}
      >
        This playbook will stop assigning outcomes to new customer interactions.
        You can add an outcome again later.
      </ConfirmationModal>

      {isTranscriptOutcome ? (
        <OutcomeFields
          outcome={displayOutcome}
          diffBaselineContext={diffBaselineOutcome?.context ?? null}
          onUpdateOutcome={handleOutcomeUpdate}
          isReadOnly={effectiveReadOnly}
          fillsHeight={fillsHeight}
        />
      ) : null}

      {activeSource && !isTranscriptOnly ? (
        <OutcomeSourceChooserDialog
          isOpen={isSourceChooserOpen}
          currentSource={activeSource}
          onOpenChange={setIsSourceChooserOpen}
          onChoose={handleSourceChange}
        />
      ) : null}
    </div>
  );

  // Optimize/Test only make sense for a transcript rubric.
  const showOptimizeTestFooter =
    !isReadOnly &&
    displayOutcome !== null &&
    isTranscriptOutcome &&
    (onOptimizeOutcome !== undefined || onTestOutcome !== undefined);
  const publishOutcome = isReadOnly ? undefined : onPublishOutcome;
  const isReportingTerminologyIncomplete =
    displayOutcome !== null && !hasCompleteOutcomeDefinition(displayOutcome);
  const isCrmFieldSelectionIncomplete =
    isFieldBackedSource &&
    (displayOutcome === null || !isFieldBackedOutcome(displayOutcome));
  const publishDisabledReason = isCrmFieldSelectionIncomplete
    ? "Select a CRM field before publishing"
    : isReportingTerminologyIncomplete
      ? incompleteOutcomeTerminologyReason
      : undefined;

  if (!isSuggestionMode) {
    const showDelete = !isReadOnly && outcome !== null;
    const footer =
      showDelete ||
      (showOptimizeTestFooter && displayOutcome) ||
      publishOutcome ? (
        <div
          className={twMerge(
            "mt-auto flex shrink-0 items-center justify-between border-t bg-white px-2 pt-4 pb-2",
            showFooterDivider ? "border-border-light" : "border-transparent",
          )}
        >
          <div className="flex items-center gap-2">
            {showDelete ? (
              <IconButton
                variant={ButtonVariant.Warning}
                size={Size.MEDIUM}
                icon={<IconTrash className="size-4" />}
                accessibleLabel="Delete"
                onClick={() => setIsRemoveConfirmationOpen(true)}
              />
            ) : null}
            {showOptimizeTestFooter && displayOutcome && onOptimizeOutcome ? (
              <Button
                variant={ButtonVariant.Secondary}
                size={Size.MEDIUM}
                icon={<IconSparkles className="size-4" />}
                onClick={() => onOptimizeOutcome(displayOutcome)}
                isDisabled={isCopilotBusy}
              >
                Optimize
              </Button>
            ) : null}
            {showOptimizeTestFooter && displayOutcome && onTestOutcome ? (
              <Button
                variant={ButtonVariant.Secondary}
                size={Size.MEDIUM}
                onClick={() => onTestOutcome(displayOutcome)}
                isDisabled={isCopilotBusy}
              >
                Test
              </Button>
            ) : null}
          </div>
          {publishOutcome ? (
            <PublishSettingButton
              settingLabel="outcome"
              hasChangesToPublish={hasChangesToPublish}
              isPublishBlocked={
                isPublishBlocked || publishDisabledReason !== undefined
              }
              disabledReason={publishDisabledReason}
              isPublishing={isPublishing}
              globalOnlyChanges={globalOnlyChanges}
              onPublish={publishOutcome}
            />
          ) : null}
        </div>
      ) : null;

    // A check-only Test produced a result without a proposed change: show the
    // editable form + footer on the left and the Results pane on the right (no
    // suggestion banner). The explicit `suggestion !== undefined` check narrows
    // the type for `suggestion.aggregates` below.
    if (suggestion !== undefined && displayOutcome) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-2 md:grid-cols-[1fr_1px_1fr]">
            <div className="flex min-h-0 flex-1 flex-col">
              <div
                ref={footerScrollContainerRef}
                className={twMerge(
                  "flex flex-col",
                  fillsHeight ? "min-h-0 flex-1 overflow-y-auto" : "shrink-0",
                )}
              >
                {formBody}
              </div>
              {footer}
            </div>
            <div
              aria-hidden
              className="-mt-2 -mb-2 hidden bg-gray-200 md:block"
            />
            <div className="flex min-h-0 flex-col gap-4 p-2">
              <h3 className="shrink-0 text-base font-semibold text-foreground">
                Results
              </h3>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                <OutcomeSuggestionResults
                  aggregates={suggestion.aggregates}
                  suggestion={suggestion}
                  outcomeDefinition={displayOutcome}
                  playbookId={playbookId}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No saved result for this outcome: single-column form, no Results pane.
    return (
      <div
        className={twMerge("flex flex-col", fillsHeight && "h-full min-h-0")}
      >
        <div
          ref={footerScrollContainerRef}
          className={twMerge(
            "flex flex-col",
            fillsHeight ? "min-h-0 flex-1 overflow-y-auto" : "shrink-0",
          )}
        >
          {formBody}
        </div>
        {footer}
      </div>
    );
  }

  // Suggestion mode: banner + read-only proposed form on the left, Results on
  // the right. `outcome` and `displayOutcome` are non-null here.
  return (
    <div className="flex h-full min-h-0 flex-col">
      <CopilotProposalBanner
        message="Showing changes suggested by Ask Alpharun."
        discardLabel="Discard changes"
        confirmLabel="Apply changes"
        onDiscard={() => onDiscardSuggestion?.()}
        onConfirm={() => {
          if (displayOutcome) onApplySuggestion?.(displayOutcome);
        }}
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-2 md:grid-cols-[1fr_1px_1fr]">
        <div className="flex min-h-0 flex-1 flex-col">{formBody}</div>
        <div aria-hidden className="-mb-2 hidden bg-gray-200 md:block" />
        <div className="flex min-h-0 flex-col gap-4 p-2">
          <h3 className="shrink-0 text-base font-semibold text-foreground">
            Results
          </h3>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {displayOutcome ? (
              <OutcomeSuggestionResults
                aggregates={suggestion.aggregates}
                suggestion={suggestion}
                outcomeDefinition={displayOutcome}
                playbookId={playbookId}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

function isFieldBackedOutcome(outcome: OutcomeDefinition): boolean {
  return (
    isFunnelOutcomeDefinition(outcome) ||
    isRevenueOutcomeDefinition(outcome) ||
    isContactCustomFieldPresenceOutcomeDefinition(outcome)
  );
}

function getOutcomeSource(
  outcome: OutcomeDefinition | null,
): OutcomeSource | null {
  if (!outcome) return null;
  return isFieldBackedOutcome(outcome)
    ? outcomeSource.field
    : outcomeSource.transcript;
}

/**
 * For an outcome suggestion, merge the proposed `context` into the current
 * outcome. Only `context` is ever changed — the type and result values/labels
 * are preserved verbatim.
 */
export function mergeSuggestionIntoOutcome(
  current: OutcomeDefinition,
  candidateDraft: PlaybookOutcomeUpdate["candidate_draft"],
): OutcomeDefinition {
  return {
    ...current,
    ...(candidateDraft.context !== undefined
      ? { context: candidateDraft.context }
      : {}),
  };
}

/**
 * True when the outcome should render with the suggestion banner + read-only
 * form + Results pane. A "check-only" run — where the candidate context equals
 * the current outcome context — does not count.
 */
export function isOutcomeInSuggestionMode(
  outcome: OutcomeDefinition,
  suggestion: SampleToolResultOutcomeEntry | undefined,
): boolean {
  if (!suggestion?.update) return false;
  return outcomeDraftChangesRubric(outcome, suggestion.update.candidate_draft);
}
